import React, { useEffect, useRef, useState } from "react";
import { api, uploadToR2 } from "../lib/api.js";
import { qFr, insightNote } from "../lib/i18n.js";
import VoiceRecorder from "./VoiceRecorder.jsx";

// Renders one question of any type, with an explicit Skip and an autosaved "saved" state.
// persist(payload) = optimistic autosave (owned by ParticipantApp); reload() re-fetches
// the session after asset changes (files, links, voice uploads).
const ordinal = (n) => n + (["th", "st", "nd", "rd"][(n % 100 - 20) % 10] || ["th", "st", "nd", "rd"][n] || "th");

// Multi-step questions: count items done, so a partial answer reads "in progress".
const AIM_TYPES = ["rating", "media", "link", "receipts", "linkwhy", "refs", "pairs", "topicmap"];
function itemCount(q, answer, assets) {
  if (!answer || answer.skipped) return 0;
  const j = answer.value_json ? JSON.parse(answer.value_json) : {};
  switch (q.type) {
    case "rating": return Object.keys(j.ratings || {}).length;
    case "media": case "link": case "receipts":
      return (assets || []).filter((a) => a.question_key === q.question_key).length;
    case "linkwhy": return (j.items || []).filter((i) => i.url || i.why).length;
    case "refs": return (j.items || []).filter((i) => i.url || i.borrow || i.avoid).length;
    case "pairs": return (j.pairs || []).length;
    case "topicmap": return (j.topics || []).length;
    default: return 1;
  }
}

export default function QuestionCard({ q, answer, assets, token, postPool, peers, peerLink, leaderName, persist, reload, t, lang, audit }) {
  const fr = lang === "fr";
  const tr = fr ? qFr(q.question_key) : {};
  const prompt = tr.prompt || q.prompt;
  const help = tr.help || q.help;
  const dataNote = insightNote(q.question_key, audit, lang);
  const label = t || ((k) => k); // fall back to key if no translator
  const answerLabel = (n) => fr ? `${n}${n === 1 ? "re" : "e"} réponse` : `${ordinal(n)} answer`;
  let state = answer ? (answer.skipped ? "skipped" : "answered") : "untouched";
  let stateLabel = label(state === "answered" ? "answered" : state);
  // The mirror is an action: "clicked" once the link is copied, then counts replies.
  if (q.type === "mirror") {
    const n = peers?.length || 0;
    if (n > 0) { state = "answered"; stateLabel = answerLabel(n); }
    else if (answer && !answer.skipped) { state = "clicked"; stateLabel = label("clicked"); }
    else if (answer?.skipped) { state = "skipped"; stateLabel = label("skipped"); }
    else { state = "untouched"; stateLabel = label("notStarted"); }
  } else if (state === "answered" && AIM_TYPES.includes(q.type)) {
    const count = itemCount(q, answer, assets);
    const aim = q.type === "rating" ? (postPool?.length || 1) : (q.config?.aim || 1);
    if (count < aim) { state = "in_progress"; stateLabel = label("inProgress"); }
  }
  const [saved, setSaved] = useState(false);

  const flash = () => { setSaved(true); setTimeout(() => setSaved(false), 1600); };

  async function save(payload) {
    const res = await persist({
      quest_key: q.quest_key, question_key: q.question_key, answer_type: q.type, ...payload,
    });
    if (res) flash();
    return res;
  }
  const skip = () => save({ action: "skip" });
  const unskip = () => save({ action: "clear" });

  return (
    <div className="question qf-reveal is-in">
      <div className="question__head">
        <div>
          <div className="question__prompt">{prompt}</div>
          {help && <div className="question__help">{help}</div>}
          {dataNote && <div className="data-note"><span className="data-note__tag" aria-hidden>◆</span>{dataNote}</div>}
          {(q.annotations || []).map((a, i) => (
            <div className="qf-note" key={i} style={{ marginTop: "var(--qf-3)" }}>{a}</div>
          ))}
        </div>
        <div className="question__badges">
          <span className={`state-tag state-tag--${state}`}>{stateLabel}</span>
          {q.deep && <span className="qf-flag">{label("deep")}</span>}
        </div>
      </div>

      <Body q={q} answer={answer} assets={assets} token={token} postPool={postPool}
            peers={peers} peerLink={peerLink} leaderName={leaderName} save={save} reload={reload}
            t={label} tr={tr} fr={fr} />

      <div className="actions">
        {state === "skipped"
          ? <button className="subtle" onClick={unskip}>{label("answerInstead")}</button>
          : <button className="subtle" onClick={skip}>{label("skip")}</button>}
        {saved && <span className="saved"><b>✓</b> {label("saved")}</span>}
      </div>
    </div>
  );
}

// ---- Body dispatch by type --------------------------------------------------
function Body(props) {
  switch (props.q.type) {
    case "slider": return <SliderInput {...props} />;
    case "choice": return <ChoiceInput {...props} />;
    case "multi": return <MultiInput {...props} />;
    case "link": return <LinkList {...props} />;
    case "media": return <Media {...props} />;
    case "linkwhy": return <LinkWhy {...props} />;
    case "refs": return <Refs {...props} />;
    case "file": return <FileUpload {...props} />;
    case "receipts": return <Receipts {...props} />;
    case "rating": return <Rating {...props} />;
    case "pairs": return <Pairs {...props} />;
    case "topicmap": return <TopicMap {...props} />;
    case "personality": return <Personality {...props} />;
    case "mirror": return <Mirror {...props} />;
    case "voice": return <VoiceFirst {...props} />;
    default: return <TextInput {...props} />;
  }
}

// ---- Text (with optional voice + optional link) -----------------------------
function TextInput({ q, answer, token, save, reload, noVoice, t }) {
  const hasAnswer = !!answer && !answer.skipped;
  // Operator draft: pre-fill the box, but keep it flagged until the participant owns it.
  const prefill = q.prefill && !hasAnswer ? q.prefill : "";
  const [val, setVal] = useState(answer?.value_text || prefill || "");
  const [confirmed, setConfirmed] = useState(hasAnswer || !q.prefill);
  const timer = useRef();
  useEffect(() => () => clearTimeout(timer.current), []);
  const onChange = (e) => {
    const v = e.target.value; setVal(v); setConfirmed(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { if (v.trim()) save({ value_text: v }); }, 700);
  };
  const useDraft = () => { setConfirmed(true); if (val.trim()) save({ value_text: val }); };
  return (
    <div className="field">
      {q.prefill && !confirmed && (
        <div className="prefill-note">
          <span>{t("prefillNote")}</span>
          <button type="button" className="qf-btn qf-btn--ghost" onClick={useDraft}>{t("prefillUse")}</button>
        </div>
      )}
      <textarea className={`input ${q.prefill && !confirmed ? "input--draft" : ""}`} value={val} onChange={onChange}
        placeholder={q.config?.allow_link ? t("typeOrLink") : t("typeHere")} />
      {val.trim() && <div className="wordcount">{t("words", val.trim().split(/\s+/).length)}</div>}
      {/* "Type or talk": voice on every open text question, plus any flagged elsewhere. */}
      {!noVoice && (q.voice_allowed || q.type === "text") && (
        <VoiceRecorder token={token} quest_key={q.quest_key} question_key={q.question_key} t={t}
          onUploaded={({ key, mime }) => api.addAsset({
            token, quest_key: q.quest_key, question_key: q.question_key, kind: "audio", r2_key: key, mime,
          }).then(() => reload && reload())} />
      )}
    </div>
  );
}

// ---- Voice-first (record two minutes), text fallback ------------------------
function VoiceFirst(props) {
  return (
    <div className="field">
      <VoiceRecorder token={props.token} quest_key={props.q.quest_key} question_key={props.q.question_key} t={props.t}
        onUploaded={({ key, mime }) => api.addAsset({
          token: props.token, quest_key: props.q.quest_key, question_key: props.q.question_key,
          kind: "audio", r2_key: key, mime,
        }).then(() => props.reload && props.reload())} />
      <p className="muted-sm" style={{ marginTop: "var(--qf-2)" }}>{props.t("preferType")}</p>
      <TextInput {...props} noVoice />
    </div>
  );
}

// ---- Slider -----------------------------------------------------------------
function SliderInput({ q, answer, save, tr }) {
  const c = q.config || {};
  const min = c.min ?? 0, max = c.max ?? 100;
  const minLabel = tr?.min_label || c.min_label;
  const maxLabel = tr?.max_label || c.max_label;
  const mid = Math.round((min + max) / 2);
  const [val, setVal] = useState(answer?.value_json ? (JSON.parse(answer.value_json).value ?? mid) : mid);
  const [touched, setTouched] = useState(!!answer && !answer.skipped);
  const commit = (v) => { setTouched(true); save({ value_json: { value: v } }); };
  const bipolar = min < 0;
  const ticks = [];
  if (max - min <= 12) for (let i = min; i <= max; i++) ticks.push(i);
  return (
    <div className="slider-wrap field">
      <div className="slider-labels"><span>{minLabel}</span>{bipolar && <span className="slider-val">{touched ? (val > 0 ? `+${val}` : val) : "·"}</span>}<span>{maxLabel}</span></div>
      <input type="range" min={min} max={max} step="1" value={val}
        onChange={(e) => { setVal(Number(e.target.value)); setTouched(true); }}
        onMouseUp={(e) => commit(Number(e.target.value))}
        onTouchEnd={(e) => commit(Number(e.target.value))} />
      <div className="slider-ticks">{ticks.map((t) => <span key={t} className={t === mid ? "on-mid" : ""} />)}</div>
    </div>
  );
}

// ---- Single choice (always with an Other + free field) ----------------------
function ChoiceInput({ q, answer, save, t, tr }) {
  const c = q.config || {};
  const j = answer?.value_json ? JSON.parse(answer.value_json) : {};
  const [choice, setChoice] = useState(j.choice ?? null);
  const [other, setOther] = useState(j.other || "");
  const pick = (opt) => { setChoice(opt); save({ value_text: opt === "Other" ? other : opt, value_json: { choice: opt, other } }); };
  const commitOther = () => save({ value_text: other, value_json: { choice: "Other", other } });
  const options = c.options || [];
  const frOpts = tr?.options;
  const disp = (opt, i) => (frOpts && frOpts[i] != null ? frOpts[i] : opt);
  const otherBtn = (
    <button type="button" className={`choice ${choice === "Other" ? "choice--on" : ""}`} onClick={() => pick("Other")}>
      {t("other")}
    </button>
  );
  return (
    <div>
      <div className={c.forced ? "forced" : "choices"}>
        {options.map((opt, i) => (
          <button key={opt} type="button" className={`choice ${choice === opt ? "choice--on" : ""}`} onClick={() => pick(opt)}>
            {disp(opt, i)}
          </button>
        ))}
        {!c.forced && otherBtn}
      </div>
      {c.forced && <div className="choices" style={{ marginTop: "var(--qf-2)" }}>{otherBtn}</div>}
      {choice === "Other" && (
        <input className="input" style={{ marginTop: "var(--qf-2)" }} placeholder={t("yourAnswer")}
          value={other} onChange={(e) => setOther(e.target.value)} onBlur={commitOther} autoFocus />
      )}
    </div>
  );
}

// ---- Multi-select (+ other, + free text) ------------------------------------
function MultiInput({ q, answer, save, t, tr }) {
  const c = q.config || {};
  const j = answer?.value_json ? JSON.parse(answer.value_json) : {};
  const [sel, setSel] = useState(j.selected || []);
  const [other, setOther] = useState(j.other || "");
  const [text, setText] = useState(j.text || "");
  const commit = (next) => save({ value_json: { selected: next.sel, other: next.other, text: next.text } });
  const toggle = (opt) => {
    const s = sel.includes(opt) ? sel.filter((x) => x !== opt) : [...sel, opt];
    setSel(s); commit({ sel: s, other, text });
  };
  const frOpts = tr?.options;
  const disp = (opt, i) => (frOpts && frOpts[i] != null ? frOpts[i] : opt);
  return (
    <div className="choices">
      {(c.options || []).map((opt, i) => (
        <label key={opt} className={`choice ${sel.includes(opt) ? "choice--on" : ""}`}>
          <input type="checkbox" checked={sel.includes(opt)} onChange={() => toggle(opt)} /> {disp(opt, i)}
        </label>
      ))}
      {c.other && (
        <input className="input" placeholder={`${t("other")}…`} value={other}
          onChange={(e) => setOther(e.target.value)} onBlur={() => commit({ sel, other, text })} />
      )}
      {c.with_text && (
        <textarea className="input" placeholder={t("yourAnswer")} value={text}
          onChange={(e) => setText(e.target.value)} onBlur={() => commit({ sel, other, text })} />
      )}
    </div>
  );
}

// ---- Repeatable link list (with async preview) ------------------------------
function LinkList({ q, assets, token, reload, t, tr }) {
  const [url, setUrl] = useState("");
  const mine = (assets || []).filter((a) => a.question_key === q.question_key && a.kind === "link");
  const add = async () => {
    if (!url.trim()) return;
    await api.addLink({ token, quest_key: q.quest_key, question_key: q.question_key, url: url.trim() });
    setUrl(""); reload();
  };
  const del = async (id) => { await api.deleteAsset(token, id); reload(); };
  return (
    <div className="field">
      <div className="hstack">
        <input className="input" placeholder={tr?.placeholder || q.config?.placeholder || "https://…"} value={url}
          onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
        <button className="qf-btn qf-btn--ghost" onClick={add}>{t("add")}</button>
      </div>
      {q.config?.aim && <p className="muted-sm" style={{ marginTop: ".4rem" }}>{t("aimAbout", q.config.aim)}</p>}
      <AssetChips items={mine} token={token} onDelete={del} t={t} />
    </div>
  );
}

function AssetChips({ items, onDelete, t }) {
  if (!items.length) return null;
  return (
    <div className="assetlist">
      {items.map((a) => (
        <div className="asset" key={a.id}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {a.kind === "link"
              ? <a href={a.external_url} target="_blank" rel="noreferrer">{a.title || a.external_url}</a>
              : <>{a.title || a.mime || a.kind} <small>{a.kind}</small></>}
          </span>
          {onDelete && <button className="link-btn" onClick={() => onDelete(a.id)}>{t ? t("removeWord") : "remove"}</button>}
        </div>
      ))}
    </div>
  );
}

// ---- Reference list: link + why ---------------------------------------------
function LinkWhy({ q, answer, save, t }) {
  const j = answer?.value_json ? JSON.parse(answer.value_json) : {};
  const [items, setItems] = useState(j.items || [{ url: "", why: "" }]);
  const commit = (next) => save({ value_json: { items: next.filter((i) => i.url || i.why) } });
  const update = (i, field, v) => { const n = items.map((it, idx) => idx === i ? { ...it, [field]: v } : it); setItems(n); };
  return (
    <div className="field">
      {items.map((it, i) => (
        <div className="topic-row" key={i}>
          <input className="input" placeholder={q.config?.url_placeholder || t("linkProfile")} value={it.url}
            onChange={(e) => update(i, "url", e.target.value)} onBlur={() => commit(items)} />
          <input className="input" style={{ marginTop: ".4rem" }} placeholder={t("whyThisPerson")}
            value={it.why} onChange={(e) => update(i, "why", e.target.value)} onBlur={() => commit(items)} />
        </div>
      ))}
      <button className="link-btn" onClick={() => setItems([...items, { url: "", why: "" }])}>{t("addAnother")}</button>
    </div>
  );
}

// ---- References: link + what you'd borrow + what you wouldn't ---------------
function Refs({ q, answer, save, t, tr }) {
  const j = answer?.value_json ? JSON.parse(answer.value_json) : {};
  const [items, setItems] = useState(j.items?.length ? j.items : [{ url: "", borrow: "", avoid: "" }]);
  const commit = (next) => save({ value_json: { items: next.filter((i) => i.url || i.borrow || i.avoid) } });
  const update = (i, f, v) => { const n = items.map((it, idx) => idx === i ? { ...it, [f]: v } : it); setItems(n); };
  return (
    <div className="field">
      {items.map((it, i) => (
        <div className="topic-row" key={i}>
          <input className="input" placeholder={tr?.url_placeholder || q.config?.url_placeholder || t("linkProfile")} value={it.url}
            onChange={(e) => update(i, "url", e.target.value)} onBlur={() => commit(items)} />
          <input className="input" style={{ marginTop: ".4rem" }} placeholder={t("couldBorrow")}
            value={it.borrow} onChange={(e) => update(i, "borrow", e.target.value)} onBlur={() => commit(items)} />
          <input className="input" style={{ marginTop: ".4rem" }} placeholder={t("wouldntCopy")}
            value={it.avoid} onChange={(e) => update(i, "avoid", e.target.value)} onBlur={() => commit(items)} />
        </div>
      ))}
      <button className="link-btn" onClick={() => setItems([...items, { url: "", borrow: "", avoid: "" }])}>{t("addAnother")}</button>
    </div>
  );
}

// ---- File picker button (custom, localized; replaces the native control) ----
function FilePicker({ multiple = true, onPick, t }) {
  return (
    <label className="filepick">
      <span className="qf-btn qf-btn--ghost">{multiple ? t("chooseFiles") : t("chooseFile")}</span>
      <input type="file" multiple={multiple} onChange={onPick} />
    </label>
  );
}

// ---- File upload ------------------------------------------------------------
function FileUpload({ q, assets, token, reload, t }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const mine = (assets || []).filter((a) => a.question_key === q.question_key && a.kind === "file");
  const multiple = q.config?.multiple !== false;
  const onPick = async (e) => {
    const files = [...e.target.files]; e.target.value = "";
    setErr(null); setBusy(true);
    try {
      for (const f of files) {
        const { key, mime } = await uploadToR2({ token, quest_key: q.quest_key, question_key: q.question_key, kind: "file", blob: f, filename: f.name });
        await api.addAsset({ token, quest_key: q.quest_key, question_key: q.question_key, kind: "file", r2_key: key, mime, title: f.name });
      }
      reload();
    } catch (ex) {
      setErr(ex.message || t("uploadError"));
    } finally { setBusy(false); }
  };
  const del = async (id) => { await api.deleteAsset(token, id); reload(); };
  return (
    <div className="field">
      <FilePicker multiple={multiple} onPick={onPick} t={t} />
      {busy && <span className="muted-sm"> {t("uploading")}</span>}
      {err && <p className="field-err">{err}</p>}
      <AssetChips items={mine} token={token} onDelete={del} t={t} />
    </div>
  );
}

// ---- Media: paste links or upload files, in one card ------------------------
function Media(props) {
  return (
    <div className="field qf-stack">
      <LinkList {...props} />
      <div>
        <span className="muted-sm">{props.t("orUpload")}</span>
        <div style={{ marginTop: ".4rem" }}><FileUpload {...props} /></div>
      </div>
    </div>
  );
}

// ---- Receipts locker: file / link / text ------------------------------------
function Receipts(props) {
  return (
    <div className="field">
      <FileUpload {...props} />
      <div className="spacer" />
      <LinkList {...props} />
      <div className="spacer" />
      <TextInput {...props} />
    </div>
  );
}

// ---- "Sounds like me" swipe over the post pool ------------------------------
function Rating({ q, answer, postPool, save, t, tr }) {
  const c = q.config || {};
  const j = answer?.value_json ? JSON.parse(answer.value_json) : {};
  const [ratings, setRatings] = useState(j.ratings || {});
  const [notes, setNotes] = useState(j.notes || {});
  const [i, setI] = useState(0);
  const pool = postPool || [];
  if (!pool.length) return <p className="muted-sm field">{t("noPosts")}</p>;
  const done = i >= pool.length;
  if (done) return <p className="saved field"><b>✓</b> {t("rated", Object.keys(ratings).length)}</p>;

  const post = pool[i];
  const label = ratings[post.id];
  const options = c.options || ["Sounds like me!", "More or less", "Not me"];
  const frOpts = tr?.options;
  const disp = (o, idx) => (frOpts && frOpts[idx] != null ? frOpts[idx] : o);
  const pick = (l) => { const next = { ...ratings, [post.id]: l }; setRatings(next); save({ value_json: { ratings: next, notes } }); };
  const setNote = (v) => setNotes({ ...notes, [post.id]: v });
  const commitNotes = () => save({ value_json: { ratings, notes } });
  const whyPrompt = label === "Not me" ? t("whyNotMe")
    : label === "More or less" ? t("whyMoreOrLess") : null;

  return (
    <div className="swipe field">
      <div className="qf-card swipe__card">{post.body}</div>
      <p className="muted-sm">{i + 1} / {pool.length}</p>
      <div className="swipe__buttons">
        {options.map((o, idx) => (
          <button key={o} className={`qf-btn ${label === o ? "qf-btn--primary" : "qf-btn--ghost"}`} onClick={() => pick(o)}>{disp(o, idx)}</button>
        ))}
      </div>
      {whyPrompt && (
        <textarea className="input" style={{ marginTop: "var(--qf-3)" }} placeholder={whyPrompt}
          value={notes[post.id] || ""} onChange={(e) => setNote(e.target.value)} onBlur={commitNotes} />
      )}
      <div style={{ marginTop: "var(--qf-3)" }}>
        <button className="qf-btn qf-btn--primary" disabled={!label} onClick={() => setI(i + 1)}>
          {i + 1 < pool.length ? t("next") : t("finish")}
        </button>
      </div>
    </div>
  );
}

// ---- Objective → action pairs -----------------------------------------------
function Pairs({ q, answer, save, t }) {
  const c = q.config || {};
  const j = answer?.value_json ? JSON.parse(answer.value_json) : {};
  const [rows, setRows] = useState(j.pairs?.length ? j.pairs : [{ left: "", right: "" }]);
  const commit = (next) => save({ value_json: { pairs: next.filter((r) => r.left || r.right) } });
  const update = (i, f, v) => { const n = rows.map((r, idx) => idx === i ? { ...r, [f]: v } : r); setRows(n); };
  return (
    <div className="field">
      {rows.map((r, i) => (
        <div className="pairs-row" key={i}>
          <input className="input" placeholder={c.left_label || t("objective")} value={r.left}
            onChange={(e) => update(i, "left", e.target.value)} onBlur={() => commit(rows)} />
          <span aria-hidden>→</span>
          <input className="input" placeholder={c.right_label || t("desiredAction")} value={r.right}
            onChange={(e) => update(i, "right", e.target.value)} onBlur={() => commit(rows)} />
        </div>
      ))}
      <button className="link-btn" onClick={() => setRows([...rows, { left: "", right: "" }])}>{t("addAnother")}</button>
    </div>
  );
}

// ---- Topic knowledge/comfort map --------------------------------------------
function TopicMap({ q, answer, save, t, tr }) {
  const c = q.config || {};
  const j = answer?.value_json ? JSON.parse(answer.value_json) : {};
  const [rows, setRows] = useState(j.topics?.length ? j.topics : [{ topic: "", a: 50, b: 50 }]);
  const commit = (next) => save({ value_json: { topics: next.filter((r) => r.topic) } });
  const update = (i, f, v) => { const n = rows.map((r, idx) => idx === i ? { ...r, [f]: v } : r); setRows(n); return n; };
  const labelA = tr?.slider_a || c.slider_a || t("knowIt");
  const labelB = tr?.slider_b || c.slider_b || t("comfort");
  return (
    <div className="field">
      {rows.map((r, i) => (
        <div className="topic-row" key={i}>
          <input className="input" placeholder={t("topic")} value={r.topic}
            onChange={(e) => update(i, "topic", e.target.value)} onBlur={() => commit(rows)} />
          <div className="slider-wrap">
            <div className="slider-labels"><span>{labelA}</span><span>{r.a}</span></div>
            <input type="range" min="0" max="100" value={r.a}
              onChange={(e) => setRows(update(i, "a", Number(e.target.value)))} onMouseUp={() => commit(rows)} onTouchEnd={() => commit(rows)} />
          </div>
          <div className="slider-wrap">
            <div className="slider-labels"><span>{labelB}</span><span>{r.b}</span></div>
            <input type="range" min="0" max="100" value={r.b}
              onChange={(e) => setRows(update(i, "b", Number(e.target.value)))} onMouseUp={() => commit(rows)} onTouchEnd={() => commit(rows)} />
          </div>
        </div>
      ))}
      <button className="link-btn" onClick={() => setRows([...rows, { topic: "", a: 50, b: 50 }])}>{t("addAnotherTopic")}</button>
    </div>
  );
}

const TYPE_NAMES = {
  INTJ: "Architect", INTP: "Logician", ENTJ: "Commander", ENTP: "Debater",
  INFJ: "Advocate", INFP: "Mediator", ENFJ: "Protagonist", ENFP: "Campaigner",
  ISTJ: "Logistician", ISFJ: "Defender", ESTJ: "Executive", ESFJ: "Consul",
  ISTP: "Virtuoso", ISFP: "Adventurer", ESTP: "Entrepreneur", ESFP: "Entertainer",
};

// ---- 16Personalities capture ------------------------------------------------
function Personality({ q, answer, assets, token, save, reload, t }) {
  const c = q.config || {};
  const j = answer?.value_json ? JSON.parse(answer.value_json) : {};
  const [type, setType] = useState(j.type || "");
  const [suffix, setSuffix] = useState(j.suffix || "");
  const [url, setUrl] = useState(j.url || "");
  const commit = (next) => save({ value_json: { type: next.type, suffix: next.suffix, url: next.url } });
  const mine = (assets || []).filter((a) => a.question_key === q.question_key && a.kind === "file");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const onFile = async (e) => {
    const f = e.target.files[0]; e.target.value = ""; if (!f) return;
    setErr(null); setBusy(true);
    try {
      const { key, mime } = await uploadToR2({ token, quest_key: q.quest_key, question_key: q.question_key, kind: "file", blob: f, filename: f.name });
      await api.addAsset({ token, quest_key: q.quest_key, question_key: q.question_key, kind: "file", r2_key: key, mime, title: f.name, is_answer: false });
      reload();
    } catch (ex) {
      setErr(ex.message || t("uploadError"));
    } finally { setBusy(false); }
  };
  return (
    <div className="field steps">
      <div className="step">
        <span className="step__n">1</span>
        <div className="step__body">
          <span className="step__t">{t("takeTest")}</span>
          <a className="qf-btn qf-btn--primary" href={c.link} target="_blank" rel="noreferrer"
             style={{ justifySelf: "start" }}>{t("openTest")}</a>
        </div>
      </div>
      <div className="step">
        <span className="step__n">2</span>
        <div className="step__body">
          <span className="step__t">{t("addResult")}</span>
          <div className="hstack">
            <select className="input" value={type} onChange={(e) => { setType(e.target.value); commit({ type: e.target.value, suffix, url }); }}>
              <option value="">{t("yourType")}</option>
              {(c.types || []).map((ty) => <option key={ty} value={ty}>{ty}{TYPE_NAMES[ty] ? ` · ${TYPE_NAMES[ty]}` : ""}</option>)}
            </select>
            <select className="input" value={suffix} onChange={(e) => { setSuffix(e.target.value); commit({ type, suffix: e.target.value, url }); }}>
              <option value="">-A / -T</option>
              {(c.suffixes || []).map((s) => <option key={s} value={s}>-{s}</option>)}
            </select>
          </div>
          <label className="muted-sm">{t("screenshotHint")}
            <div style={{ marginTop: ".3rem" }}>
              <FilePicker multiple={false} onPick={onFile} t={t} />
              {busy && <span className="muted-sm"> {t("uploading")}</span>}
            </div>
          </label>
          {err && <p className="field-err">{err}</p>}
          <AssetChips items={mine} token={token} onDelete={async (id) => { await api.deleteAsset(token, id); reload(); }} t={t} />
          <input className="input" placeholder={t("resultLink")}
            value={url} onChange={(e) => setUrl(e.target.value)} onBlur={() => commit({ type, suffix, url })} />
        </div>
      </div>
    </div>
  );
}

// Ready-to-send messages, participant's own voice, friendly. {link} is appended.
const MIRROR_MSG = {
  en: {
    label: "English",
    subject: "A quick favour for my LinkedIn?",
    body: (link) => `Hi! I'm working on my LinkedIn strategy, and the person helping me needs a few things about me. Could you answer this very short form (today or tomorrow if you can)? Thanks so much!\n\n${link}`,
  },
  fr: {
    label: "Français",
    subject: "Un petit coup de main pour ma présence LinkedIn ?",
    body: (link) => `Salut ! Je travaille ma stratégie LinkedIn, et la personne qui m'accompagne aurait besoin de quelques infos sur moi. Tu peux répondre à ce tout petit questionnaire (aujourd'hui ou demain si possible) ? Merci beaucoup !\n\n${link}`,
  },
  pt: {
    label: "Português",
    subject: "Uma ajudinha rápida para o meu LinkedIn?",
    body: (link) => `Oi! Estou trabalhando na minha estratégia de LinkedIn, e a pessoa que me acompanha precisa de algumas informações sobre mim. Você pode responder a este questionário bem curtinho (hoje ou amanhã, se possível)? Muito obrigado(a)!\n\n${link}`,
  },
  es: {
    label: "Español",
    subject: "¿Una ayudita rápida para mi LinkedIn?",
    body: (link) => `¡Hola! Estoy trabajando en mi estrategia de LinkedIn, y la persona que me acompaña necesita algunas cosas sobre mí. ¿Puedes responder este cuestionario muy corto (hoy o mañana si puedes)? ¡Muchas gracias!\n\n${link}`,
  },
};

// What the people you ask will see (participant preview). Mirrors the 7 peer
// questions in peer.js. Keyed by language.
const MIRROR_PREVIEW = {
  en: [
    "How would you describe the way {name} communicates day to day?",
    "What makes people warm to {name}, or want to follow them?",
    "Any verbal tics or turns of phrase you've noticed in how {name} talks?",
    "When {name} talks about their work, what lights them up?",
    "What does {name} explain better than anyone you know?",
    "Anything {name} believes that most people in their field don't?",
    "If you had to get someone to follow {name}, what would you say in one line?",
  ],
  fr: [
    "Comment décrirais-tu la façon dont {name} communique au quotidien ?",
    "Qu'est-ce qui fait que les gens s'attachent à {name}, ou ont envie de la/le suivre ?",
    "Des tics de langage ou tournures que tu as remarqués chez {name} ?",
    "Quand {name} parle de son travail, qu'est-ce qui l'anime ?",
    "Qu'est-ce que {name} explique mieux que personne que tu connaisses ?",
    "Une chose que {name} croit et que la plupart des gens de son domaine ne croient pas ?",
    "Si tu devais convaincre quelqu'un de suivre {name}, que dirais-tu en une phrase ?",
  ],
};

const MIRROR_LANGS = [["en", "EN"], ["fr", "FR"], ["pt", "PT"], ["es", "ES"]];

// ---- Mirror action ----------------------------------------------------------
function Mirror({ q, answer, peers, peerLink, leaderName, save, t, fr }) {
  const sent = answer && !answer.skipped;
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedMsg, setCopiedMsg] = useState(false);
  // The message template defaults to the participant's UI language.
  const [msgLang, setMsgLang] = useState(fr ? "fr" : "en");
  const [draft, setDraft] = useState(MIRROR_MSG[fr ? "fr" : "en"].body(peerLink));
  const [showPreview, setShowPreview] = useState(false);
  const first = (leaderName || "them").split(" ")[0];

  // Reset the editable draft when the message language changes.
  useEffect(() => { setDraft(MIRROR_MSG[msgLang].body(peerLink)); }, [msgLang, peerLink]);

  const markSent = () => { if (!sent) save({ value_text: "sent" }); };
  const copyLink = async () => {
    await navigator.clipboard.writeText(peerLink);
    setCopiedLink(true); setTimeout(() => setCopiedLink(false), 1500); markSent();
  };
  const copyMsg = async () => {
    await navigator.clipboard.writeText(draft);
    setCopiedMsg(true); setTimeout(() => setCopiedMsg(false), 1500); markSent();
  };
  const nPeers = peers?.length || 0;
  const preview = MIRROR_PREVIEW[fr ? "fr" : "en"];

  return (
    <div className="field mirror">
      <div className="steps">
        <div className="step">
          <span className="step__n">1</span>
          <div className="step__body">
            <span className="step__t">{t("yourLink")}</span>
            <div className="hstack">
              <input className="input" readOnly value={peerLink} onFocus={(e) => e.target.select()} />
              <button className="qf-btn qf-btn--primary copybtn" onClick={copyLink}>{copiedLink ? t("copied") : t("copyLink")}</button>
            </div>
          </div>
        </div>
        <div className="step">
          <span className="step__n">2</span>
          <div className="step__body">
            <div className="hstack qf-between">
              <span className="step__t">{t("msgLabel")}</span>
              <div className="seg" role="group" aria-label="Message language">
                {MIRROR_LANGS.map(([code, short]) => (
                  <button key={code} className={msgLang === code ? "on" : ""} aria-pressed={msgLang === code} onClick={() => setMsgLang(code)}>{short}</button>
                ))}
              </div>
            </div>
            <textarea className="input" value={draft} onChange={(e) => setDraft(e.target.value)} style={{ minHeight: 150 }} />
            <div className="actions">
              <button className="qf-btn qf-btn--ghost" onClick={copyMsg}>{copiedMsg ? t("copied") : t("copyMsg")}</button>
              <span className="muted-sm">{t("msgTweak")}</span>
            </div>
          </div>
        </div>
      </div>

      <div>
        <button className="subtle" onClick={() => setShowPreview((v) => !v)}>
          {showPreview ? t("hideAsked") : t("seeAsked")}
        </button>
        {showPreview && (
          <ol className="muted-sm mirror__preview">
            {preview.map((p, i) => <li key={i}>{p.replace(/\{name\}/g, first)}</li>)}
          </ol>
        )}
      </div>

      <div className="mirror__replies">
        <span className="qf-pill"><strong>{nPeers}</strong> {t("repliedSoFar")}</span>
        {nPeers > 0 && <ul className="muted-sm">{peers.map((p, i) => <li key={i}>{p.respondent_name}, {p.respondent_email}</li>)}</ul>}
      </div>
    </div>
  );
}
